import { fail, ok, Result } from '../shared/Utility/result';

export enum LengthUnit {
  Metres = 'm',
  Centimetres = 'cm',
  Feet = 'ft',
  Inches = 'in',
}

export const LENGTH_UNITS: readonly LengthUnit[] = [LengthUnit.Metres, LengthUnit.Centimetres, LengthUnit.Feet, LengthUnit.Inches];

export const UNIT_LABELS: Readonly<Record<LengthUnit, string>> = {
  [LengthUnit.Metres]: 'metres',
  [LengthUnit.Centimetres]: 'centimetres',
  [LengthUnit.Feet]: 'feet',
  [LengthUnit.Inches]: 'inches',
};

const UNIT_DECIMALS: Readonly<Record<LengthUnit, number>> = {
  [LengthUnit.Metres]: 2,
  [LengthUnit.Centimetres]: 1,
  [LengthUnit.Feet]: 2,
  [LengthUnit.Inches]: 1,
};

export interface ScaleCalibration {
  readonly pixelsPerUnit: number;
  readonly unit: LengthUnit;
  readonly referencePixels: number;
  readonly referenceLength: number;
}

export interface ScenePoint {
  readonly x: number;
  readonly y: number;
}

export const MIN_REFERENCE_PIXELS = 2;
export const NOT_TO_SCALE = 'Not to scale';

export function distance(a: ScenePoint, b: ScenePoint): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

export function calibrate(referencePixels: number, referenceLength: number, unit: LengthUnit): Result<ScaleCalibration> {
  if (!Number.isFinite(referencePixels) || referencePixels < MIN_REFERENCE_PIXELS) {
    return fail('The reference line is too short to set a scale.');
  }
  if (!Number.isFinite(referenceLength) || referenceLength <= 0) {
    return fail('Enter the real length of the line as a number greater than zero.');
  }
  if (!LENGTH_UNITS.includes(unit)) {
    return fail('Choose a unit.');
  }
  return ok({ pixelsPerUnit: referencePixels / referenceLength, unit, referencePixels, referenceLength });
}

export function pixelsToLength(pixels: number, scale: ScaleCalibration): number {
  return pixels / scale.pixelsPerUnit;
}

export function formatLength(value: number, unit: LengthUnit): string {
  return `${value.toFixed(UNIT_DECIMALS[unit])} ${unit}`;
}

export function formatMeasurement(pixels: number, scale: ScaleCalibration | null): string {
  return scale ? formatLength(pixelsToLength(pixels, scale), scale.unit) : NOT_TO_SCALE;
}

export const PHOTO_SCALE_CAVEAT = 'On a photograph, lengths are only meaningful in the plane of the reference object; anything nearer or farther from the camera is not measured correctly.';

export function describeScale(scale: ScaleCalibration | null, exportMultiplier: number = 1, onPhoto: boolean = false): string {
  if (!scale) {
    return `${NOT_TO_SCALE}. No reference measurement was entered.`;
  }
  const multiplier = Number.isFinite(exportMultiplier) && exportMultiplier > 0 ? exportMultiplier : 1;
  const imagePixels = multiplier === 1 ? '' : ` In the attached image (${multiplier.toFixed(2)} x the page) that is ${(scale.pixelsPerUnit * multiplier).toFixed(2)} px per ${scale.unit}.`;
  return (
    `Set from a reference measurement drawn on this sketch: ${scale.referencePixels.toFixed(1)} px on the page = ` +
    `${formatLength(scale.referenceLength, scale.unit)} (${scale.pixelsPerUnit.toFixed(2)} px per ${scale.unit}).${imagePixels} ` +
    'Measurements are only as accurate as the reference and the drawing.' +
    (onPhoto ? ` ${PHOTO_SCALE_CAVEAT}` : '')
  );
}

export function isScaleCalibration(value: unknown): value is ScaleCalibration {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const v = value as Record<string, unknown>;
  return (
    typeof v['pixelsPerUnit'] === 'number' &&
    Number.isFinite(v['pixelsPerUnit']) &&
    (v['pixelsPerUnit'] as number) > 0 &&
    LENGTH_UNITS.includes(v['unit'] as LengthUnit) &&
    typeof v['referencePixels'] === 'number' &&
    typeof v['referenceLength'] === 'number'
  );
}

export function snapAngle(from: ScenePoint, to: ScenePoint, stepDegrees: number): ScenePoint {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);
  if (length === 0) {
    return to;
  }
  const step = (stepDegrees * Math.PI) / 180;
  const angle = Math.round(Math.atan2(dy, dx) / step) * step;
  return { x: from.x + Math.cos(angle) * length, y: from.y + Math.sin(angle) * length };
}
